import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ICONS from "../../../assets/Icons";
import TertiaryTopRoundButton from "../../../components/_utils/CommonButtons/TertiaryTopRoundButton/TertiaryTopRoundButton";
import CommonCardDashboard from "../../../components/_utils/CommonCardDashboard";
import WeeklySchedulesCard from "../../../components/_utils/WeeklySchedulesCard";
import {
  channelsViewAll,
  favouriteNotification,
  notificationsViewAll,
  updateNotificationInStore,
} from "../../../redux/features/Notifications";
import {
  disableSwitcher,
  enableSwitcher,
} from "../../../redux/features/multiEntitySwitcher/multiEntitySwitcher";
import {
  GlobalConfiguration,
  TimelineConfiguration,
} from "../../../utils/configurationProvider";
import CommonToast from "../../../components/_utils/CommonToast";
import SVGComponent from "../../../components/_utils/SVGComponent";
import Loader from "../../../components/Loader/Index";
import CommonMoreActionsCard from "../../../components/_utils/CommonMoreActionsCard";
import { toast } from "react-toastify";
import useDynamicRouting from "../../../utils/useDynamicRouting";
import TEXT from "../../../utils/globalText";

const NotificationDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Custom hook for dynamic routing based on notification type and ID.
  const { handleDynamicRouting } = useDynamicRouting();

  // Controls the initial dashboard loading state. Used to show loaders while channels and notification data are being fetched.
  const [loading, setLoading] = useState(true);

  // Stores the notification summary counts displayed in the dashboard cards. The values represent total unread and favourited notifications.
  const [notificationSummary, setNotificationSummary] = useState({
    unread: GlobalConfiguration.lengthCheckZero,
    favourite: GlobalConfiguration.lengthCheckZero,
  });

  // Controls loading specifically for the notification summary/count cards. This is separate from the overall dashboard loading state.
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Gets the services assigned to the current user/entity.
  // Services are used to determine whether notification and channel features should be available.
  const services = useSelector((state) => state.refreshToken.services);

  // Gets the currently selected entity/marker from the multi-entity switcher. Changing this value triggers the dashboard data to be fetched again.
  const { markerData } = useSelector((s) => s.multiEntitySwitcherSlice);

  const {
    channelsData,
    channelsLoading,
    notificationsData,
    notificationsLoading,
  } = useSelector((state) => state?.notificationsSlice);

  // Stores the favourite notifications separately from the main notification
  // response so the dashboard can display up to 50 favourites while keeping
  // additional fetched favourites available in memory.
  const [favouriteNotifications, setFavouriteNotifications] = useState([]);

  // Stores the ID of the notification currently undergoing a favourite/unfavourite animation.
  const [animatingNotificationId, setAnimatingNotificationId] = useState(null);

  // Stores which animation should be applied to the notification. Possible values used here are "slideOut" and "slideIn".
  const [animationType, setAnimationType] = useState(null);

  // Stores information about an unfavourite action that is waiting for the undo-toast period to finish.
  const pendingUnfavouriteToastRef = useRef(null);

  // Clears the currently pending unfavourite operation.
  // This is mainly used when the dashboard is unmounted or when another unfavourite operation replaces the previous pending operation.
  // If an operation is pending, the notification is permanently unfavourited, the sessionStorage entry is removed and the related toast is dismissed.
  const clearPendingUnfavouriteToast = () => {
    const pending = pendingUnfavouriteToastRef.current;

    if (!pending) return;

    const pendingNotificationId = pending.notificationId;

    pending.shouldCallApi = false;

    pendingUnfavouriteToastRef.current = null;
    sessionStorage.removeItem("pendingUnfavourite");

    if (pendingNotificationId) {
      dispatch(
        favouriteNotification({
          id: pendingNotificationId,
          favourite: false,
        }),
      );
    }

    if (pending.toastId) {
      toast.dismiss(pending.toastId);
    }
  };

  // Gets the unread notification count from the summary.
  const unreadNotificationsCount =
    notificationSummary.unread > 99999
      ? "99999+"
      : (notificationSummary.unread ??
        GlobalConfiguration.dashboardPrimaryCount);

  // Gets the favourite notification count from the summary.
  const favNotificationsCount =
    notificationSummary.favourite > 99999
      ? "99999+"
      : (notificationSummary.favourite ??
        GlobalConfiguration.dashboardPrimaryCount);

  //Fetches all notification channels available to the current entity/user.
  //The Redux action updates channelsData and channelsLoading in the notifications Redux slice.
  async function getChannelsData() {
    try {
      await dispatch(channelsViewAll({})).unwrap();
    } catch (error) {
      console.error("Channels fetch failed", error);
    }
  }

  //Fetches the notification summary used by the dashboard.
  async function getNotificationsData() {
    try {
      setSummaryLoading(true);

      const response = await dispatch(
        notificationsViewAll({ active: true }),
      ).unwrap();

      setNotificationSummary({
        unread: Number(
          response?.summary?.unread ?? GlobalConfiguration.lengthCheckZero,
        ),
        favourite: Number(
          response?.summary?.favourite ?? GlobalConfiguration.lengthCheckZero,
        ),
      });
    } catch (error) {
      console.error("Notifications fetch failed", error);

      setNotificationSummary({
        unread: GlobalConfiguration.lengthCheckZero,
        favourite: GlobalConfiguration.lengthCheckZero,
      });
    } finally {
      setSummaryLoading(false);
    }
  }

  //Fetches favourited notifications separately from the main notification request.
  async function getFavouriteNotificationsData() {
    try {
      const response = await dispatch(
        notificationsViewAll({
          favourite: true,
          active: true,
          page_no: GlobalConfiguration.lengthCheckOne,
          page_size: 100,
        }),
      ).unwrap();

      const favouriteList = Array.isArray(response?.notifications)
        ? response.notifications
        : Array.isArray(response?.data)
          ? response.data
          : [];

      setFavouriteNotifications(favouriteList);
    } catch (error) {
      console.error("Favourite notifications fetch failed", error);
      setFavouriteNotifications([]);
    }
  }

  //Performs all initial dashboard data loading.
  async function onMount() {
    setLoading(true);

    const pendingNotificationId = sessionStorage.getItem("pendingUnfavourite");

    if (pendingNotificationId) {
      await dispatch(
        favouriteNotification({
          id: pendingNotificationId,
          favourite: false,
        }),
      );

      sessionStorage.removeItem("pendingUnfavourite");
    }

    await getChannelsData();
    await getNotificationsData();
    await getFavouriteNotificationsData();
    setLoading(false);
  }

  //Dashboard lifecycle effect. Enables the multi-entity switcher while this page is active and disables it when the dashboard is unmounted.
  //Cleanup also makes sure that any pending unfavourite toast operation is handled before leaving the page.
  useEffect(() => {
    dispatch(enableSwitcher());

    return () => {
      clearPendingUnfavouriteToast();
      dispatch(disableSwitcher());
    };
  }, []);

  //Entity-change/data initialization effect. Whenever markerData changes, the dashboard is reloaded so that channels,
  //notification summary and favourites correspond to the newly selected entity.
  useEffect(() => {
    (async () => {
      await onMount();
    })();
  }, [markerData]);

  // Normalizes the channel API response so the UI can work with either response.channels or response.data.
  const channels = Array?.isArray(channelsData?.channels)
    ? channelsData?.channels
    : Array?.isArray(channelsData?.data)
      ? channelsData?.data
      : [];

  //Formats dashboard counts according to the application's display rules.
  //If the user does not have the requested service and the count is zero,"N/A" is displayed.
  //Zero counts are displayed using the configured placeholder. Larger values are formatted with the application's abbreviated number format.
  const formatPendingCount = (count, userServices, serviceName) => {
    const hasService = (userServices || []).includes(serviceName);

    if (!hasService && count === GlobalConfiguration.lengthCheckZero) {
      return "N/A";
    }

    if (count === GlobalConfiguration?.lengthCheckZero)
      return GlobalConfiguration.dashboardPrimaryCount;

    const digitCount = count.toString().length;

    if (digitCount <= GlobalConfiguration?.categoryminLength) {
      return count
        .toLocaleString("en-IN")
        .padStart(TimelineConfiguration?.barWidthMedium, "0");
    }

    const maxForDigits =
      Math.pow(
        GlobalConfiguration?.panLength,
        digitCount - GlobalConfiguration?.lengthCheckOne,
      ) - GlobalConfiguration?.lengthCheckOne;

    return `${maxForDigits.toLocaleString("en-IN")}+`;
  };

  // Ensures services is always treated as an array before processing it.
  const safeServices = Array.isArray(services) ? services : [];

  // Normalizes service names so service checks are case-insensitive and unaffected by leading/trailing whitespace.
  const normalizedServices = safeServices
    .map((service) =>
      typeof service === "string"
        ? service.trim().toLowerCase()
        : String(service || "")
            .trim()
            .toLowerCase(),
    )
    .filter(Boolean);

  // Determines the favourite count shown on the dashboard. Only Admin or Notify users are allowed to see the actual count.
  const displayfavNotificationsCount =
    normalizedServices.includes("notify") ||
    normalizedServices.includes("admin")
      ? favNotificationsCount
      : "N/A";

  // Determines the unread count shown on the dashboard.Only Admin or Notify users are allowed to see the actual count.
  const displayUnreadNotificationsCount =
    normalizedServices.includes("notify") ||
    normalizedServices.includes("admin")
      ? unreadNotificationsCount
      : "N/A";

  //Handles navigation to the Create Channel page.
  const handleCreateChannel = () => {
    if (
      normalizedServices.includes("admin") ||
      normalizedServices.includes("cchannel")
    ) {
      navigate("/home/notification_dashboard/create_channel");
      return;
    } else {
      navigate("/feature_not_assigned", {
        state: {
          unavailableService: "Admin,CChannel",
        },
      });
    }
  };

  // Handles navigation to the View Channels page.
  const handleViewChannels = () => {
    if (
      normalizedServices.includes("admin") ||
      normalizedServices.includes("vchannel")
    ) {
      navigate("/home/notification_dashboard/channel_view_all");
      return;
    } else {
      navigate("/feature_not_assigned", {
        state: { unavailableService: "Admin,VChannel" },
      });
    }
  };

  // Normalizes the notification API response so the component can support both notificationsData.notifications and notificationsData.data.
  const notifications =
    notificationsData?.notifications || notificationsData?.data || [];

  const displayedNotifications = favouriteNotifications.slice(0, 50);

  const hasNotifyService =
    services?.includes("Admin") || services?.includes("Notify");

  //Handles favouriting and unfavouriting notifications.
  //When unfavouriting from the favourites dashboard, the notification is immediately removed from the UI and an Undo toast is displayed.
  //The actual API operation is delayed until the toast is dismissed unless the user clicks Undo.
  const handleMarkUnmarkFavourite = async (
    notificationId,
    currentFavouriteStatus,
  ) => {
    if (!notificationId) return;

    if (currentFavouriteStatus) {
      const currentNotification = notifications.find(
        (notification) => notification.id === notificationId,
      );

      const originalIndex = favouriteNotifications.findIndex(
        (notification) =>
          (notification?.id || notification?.notification_id) ===
          notificationId,
      );

      const previousPending = pendingUnfavouriteToastRef.current;

      if (previousPending) {
        previousPending.shouldCallApi = false;

        if (previousPending.toastId) {
          toast.dismiss(previousPending.toastId);
        }

        await dispatch(
          favouriteNotification({
            id: previousPending.notificationId,
            favourite: false,
          }),
        );
      }

      pendingUnfavouriteToastRef.current = {
        notificationId,
        notification: currentNotification,
        originalIndex,
        toastId: null,
        shouldCallApi: true,
      };

      sessionStorage.setItem("pendingUnfavourite", notificationId);

      setFavouriteNotifications((prevNotifications) =>
        prevNotifications.filter(
          (notification) =>
            (notification?.id || notification?.notification_id) !==
            notificationId,
        ),
      );

      dispatch(
        updateNotificationInStore({
          response: {
            ...currentNotification,
            id: notificationId,
            favourite: false,
          },
          stateToUpdate: "notificationsData",
        }),
      );

      const toastId = CommonToast({
        text: "Notification unfavourited successfully.",
        variant: "var(--primary-nav)",
        buttonNeeded: true,
        btnType: "btnWithoutIcon",
        btnProps: {
          btnText: "Undo",
          handleFunction: () => {
            handleUndoFavourite(notificationId);
          },
          enabledTextColor: "var(--text-primary)",
          enabledBgColor: "var(--primary-card)",
        },
        toastOptions: {
          onClose: () => {
            const pending = pendingUnfavouriteToastRef.current;

            if (!pending?.shouldCallApi) return;

            const latestNotificationId = pending.notificationId;

            pending.shouldCallApi = false;
            pendingUnfavouriteToastRef.current = null;

            sessionStorage.removeItem("pendingUnfavourite");

            dispatch(
              favouriteNotification({
                id: latestNotificationId,
                favourite: false,
              }),
            );
          },
        },
      });

      pendingUnfavouriteToastRef.current.toastId = toastId;

      return;
    }

    try {
      const response = await dispatch(
        favouriteNotification({
          id: notificationId,
          favourite: !currentFavouriteStatus,
        }),
      );

      if (response?.payload) {
        dispatch(
          updateNotificationInStore({
            response: response.payload,
            stateToUpdate: "notificationsData",
          }),
        );
      }

      return response;
    } catch (error) {
      console.error(error);
    }
  };

  //Restores a notification after the user clicks Undo on the unfavourite toast.
  const handleUndoFavourite = async (notificationId) => {
    const pending = pendingUnfavouriteToastRef.current;
    if (!pending || pending.notificationId !== notificationId) {
      return;
    }
    pending.shouldCallApi = false;

    sessionStorage.removeItem("pendingUnfavourite");

    pendingUnfavouriteToastRef.current = null;
    if (pending.toastId) {
      toast.dismiss(pending.toastId);
    }

    setAnimatingNotificationId(notificationId);
    setAnimationType("slideIn");

    dispatch(
      updateNotificationInStore({
        response: {
          ...pending.notification,
          id: notificationId,
          favourite: true,
        },
        stateToUpdate: "notificationsData",
      }),
    );

    setFavouriteNotifications((prevNotifications) => {
      const restoredNotification = {
        ...pending.notification,
        id: notificationId,
        favourite: true,
      };

      const updatedNotifications = prevNotifications.filter(
        (notification) =>
          (notification?.id || notification?.notification_id) !==
          notificationId,
      );

      updatedNotifications.splice(
        Math.min(pending.originalIndex, updatedNotifications.length),
        0,
        restoredNotification,
      );

      return updatedNotifications;
    });

    setTimeout(() => {
      setAnimatingNotificationId(null);
      setAnimationType(null);
    }, 500);
  };

  //Starts the slide-out animation when a favourite notification is being unfavourited.
  const triggerFavouriteAnimation = (notification) => {
    if (!notification?.id || !notification?.favourite) {
      return;
    }

    if (animatingNotificationId === notification.id) {
      return;
    }

    setAnimatingNotificationId(notification.id);
    setAnimationType("slideOut");

    setTimeout(() => {
      handleMarkUnmarkFavourite(notification.id, notification.favourite);
    }, 300);

    setTimeout(() => {
      setAnimatingNotificationId(null);
      setAnimationType(null);
    }, 800);
  };

  //Returns the correct channel icon based on the provider.
  const getChannelIcon = (provider) => {
    switch (provider?.toLowerCase()) {
      case "gchat":
        return ICONS.GOOGLE_CHAT;

      case "slack":
        return ICONS.SLACK;

      case "teams":
        return ICONS.MICROSOFT_TEAMS;

      default:
        return ICONS.WEB_HOOK;
    }
  };

  //Determines whether the user is allowed to view channels.
  const canViewChannels =
    normalizedServices.includes("admin") ||
    normalizedServices.includes("vchannel");

  return (
    <div
      style={{ height: "var(--full-height)" }}
      className="bg-primary-bg w-width-full  overflow-y-auto pb-padding-md"
    >
      <div className="h-navbar-height Qsmd:h-navbar-breadcrumb-height"></div>
      <div className="Qsmd:hidden fixed w-width-full p-padding-sm top-top-base bg-primary-nav flex items-center z-z-index-fixed-ui rounded-b-border-radius-md shadow-box-shadow-bottom">
        <div
          className="pl-padding-sm pr-padding-md cursor-pointer"
          onClick={() => navigate(GlobalConfiguration.navigate_back)}
        >
          <SVGComponent
            url={ICONS?.ARROW_LEFT_WHITE}
            color="var(--text-primary)"
            height={"var(--icon-sm)"}
            width={"var(--icon-sm)"}
          />
        </div>
        <div className="flex flex-col">
          <p className="text-font-md text-text-primary font-normal">
            Notification
          </p>
          <p className="text-font-xs text-text-primary font-normal">
            {TEXT?.DASHBOARD}
          </p>
        </div>
      </div>
      <div className="px-padding-md Qlg:px-padding-base Qsmd:pb-padding-base Qsmd:mb-margin-base pb-padding-xl mb-margin-xl">
        <div className="flex flex-col mt-margin-md lg:mx-auto max-w-tab-width-normal w-width-full Qlg:flex-row Qlg:px-padding-base lg:mb-margin-base Qlg:relative gap-gap-md lg:gap-gap-base">
          <div className="w-width-full Qlg:w-dashboard-left-card-width flex flex-col">
            <div className="w-width-full bg-primary-card rounded-border-radius-md relative overflow-hidden">
              <h2 className="bg-primary-card flex items-center justify-between pl-padding-md text-text-secondary transition-all duration-animation-medium">
                <div className="flex items-center text-sm font-semibold">
                  <img
                    src={ICONS?.NOTIFICATION}
                    alt="notification header"
                    className="w-icon-sm h-icon-sm"
                  />
                  <p className="text-font-sm font-semibold font-sans text-text-primary ml-margin-sm">
                    {TEXT?.NOTIFICATION}s
                  </p>
                </div>

                <div className="flex">
                  <div className="border-r-border my-margin-sm border-primary-bg"></div>
                  <TertiaryTopRoundButton
                    btnText={TEXT?.VIEW_ALL}
                    icon={ICONS?.ARROW_RIGHT_WHITE}
                    imgColor="var(--text-primary)"
                    btnType="button"
                    hoverOption="white"
                    handleFunction={() => {
                      if (
                        !(
                          notificationsData?.notifications ||
                          notificationsData?.data
                        )?.length
                      ) {
                        return;
                      }

                      if (
                        services?.includes("Admin") ||
                        services?.includes("Notify")
                      ) {
                        if (!notificationsLoading) {
                          navigate(
                            "/home/notification_dashboard/view_notifications",
                            { state: { read: "all" } },
                          );
                        }
                      } else {
                        navigate("/feature_not_assigned", {
                          state: {
                            unavailableService: "Admin, Notify",
                          },
                        });
                      }
                    }}
                    isDisabled={
                      summaryLoading
                        ? true
                        : services?.includes("Admin") ||
                            services?.includes("Notify")
                          ? !(
                              notificationsData?.notifications ||
                              notificationsData?.data
                            )?.length
                          : true
                    }
                    enabledTextColor="var(--text-primary)"
                    enabledBgColor="var(--primary-card)"
                    disabledBgColor="transparent"
                  />
                </div>
              </h2>
              <div className="flex flex-col Qlg:flex-row gap-gap-base p-padding-base bg-primary-nav">
                <CommonCardDashboard
                  label={TEXT?.FAVOURITES}
                  count={displayfavNotificationsCount}
                  color="var(--awaiting)"
                  loader={loading || summaryLoading}
                  onClick={() => {
                    if (
                      services?.includes("Admin") ||
                      services?.includes("Notify")
                    ) {
                      if (!notificationsLoading)
                        navigate(
                          "/home/notification_dashboard/view_notifications",
                          {
                            state: {
                              fav: "Show only favourite",
                              read: "all",
                            },
                          },
                        );
                    } else {
                      navigate("/feature_not_assigned", {
                        state: {
                          unavailableService: "Admin, Notify",
                        },
                      });
                    }
                  }}
                  onServiceUnavailable={() =>
                    navigate("/feature_not_assigned", {
                      state: { unavailableService: ["Notify", "Admin"] },
                    })
                  }
                />
                <div className="Qlg:border-r-border border-t-border border-primary-bg " />
                <CommonCardDashboard
                  label={TEXT?.UNREAD}
                  count={displayUnreadNotificationsCount}
                  color="var(--pending)"
                  loader={loading || summaryLoading}
                  onClick={() => {
                    if (
                      services?.includes("Admin") ||
                      services?.includes("Notify")
                    ) {
                      if (!notificationsLoading)
                        navigate(
                          "/home/notification_dashboard/view_notifications",
                          {
                            state: {
                              read: false,
                            },
                          },
                        );
                    } else {
                      navigate("/feature_not_assigned", {
                        state: {
                          unavailableService: "Admin, Notify",
                        },
                      });
                    }
                  }}
                  onServiceUnavailable={() =>
                    navigate("/feature_not_assigned", {
                      state: { unavailableService: ["Notify", "Admin"] },
                    })
                  }
                />
              </div>
            </div>

            <div className="w-width-full mt-margin-md bg-primary-card rounded-t-border-radius-md rounded-b-border-radius-md overflow-hidden">
              <h2 className="flex items-center justify-between px-padding-md py-padding-sm text-sm font-semibold text-text-secondary transition-all duration-animation-medium rounded-t-border-radius-md">
                <span className="flex items-center text-font-sm font-semibold text-text-primary ">
                  <div className="mr-margin-sm">
                    <SVGComponent
                      url={ICONS?.FAVOURITE_FILLED}
                      width="var(--icon-sm)"
                      height="var(--icon-sm)"
                    />
                  </div>
                  {TEXT?.FAVOURITED} {TEXT?.NOTIFICATION?.toLowerCase()}s
                </span>
              </h2>
              <div className="border-t-border border-primary-bg mx-margin-md"></div>

              <div className="relative w-width-full overflow-hidden block pt-padding-sm pb-padding-sm">
                <div className="h-[var(--recent-notification-box)] overflow-y-auto px-padding-md">
                  {loading ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader
                        height="var(--icon-lg)"
                        width="var(--icon-lg)"
                      />
                    </div>
                  ) : displayedNotifications.length >
                    GlobalConfiguration.lengthCheckZero ? (
                    <div className="flex flex-col gap-gap-sm">
                      {displayedNotifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`bg-primary-nav global-hover-white relative z-z-index-base cursor-pointer rounded-border-radius-sm ${
                            animatingNotificationId === notification.id
                              ? animationType === "slideOut"
                                ? "animate-slideOutFromRight"
                                : animationType === "slideIn"
                                  ? "animate-slideInFromRight"
                                  : ""
                              : ""
                          }`}
                        >
                          <div
                            className="flex items-center justify-between p-padding-sm gap-gap-sm"
                            onClick={() => {
                              if (
                                services?.includes("Admin") ||
                                services?.includes("Notify")
                              ) {
                                navigate(
                                  handleDynamicRouting(
                                    notification?.eventable_type,
                                    notification?.eventable_id ||
                                      notification?.eventable_ids,
                                  ),
                                );
                              } else {
                                navigate("/feature_not_assigned", {
                                  state: {
                                    unavailableService: "Admin, Notify",
                                  },
                                });
                              }
                            }}
                          >
                            <div className="flex flex-col gap-gap-xs">
                              <p className="text-text-primary text-font-sm font-semibold">
                                {notification.title || "Notification"}
                              </p>
                              <p className="text-text-secondary text-font-sm font-normal">
                                {notification.message || "New message"}
                              </p>
                            </div>

                            <div
                              className="bg-primary-card rounded-border-radius-md p-padding-xs justify-center cursor-pointer relative z-z-index-base global-hover-white flex-shrink-0"
                              onClick={(e) => {
                                if (
                                  services?.includes("Admin") ||
                                  services?.includes("Notify")
                                ) {
                                  e.stopPropagation();
                                  triggerFavouriteAnimation(notification);
                                }
                              }}
                            >
                              <img
                                src={ICONS?.FAVOURITE_FILLED}
                                alt=""
                                className="w-icon-sm h-icon-sm"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-text-secondary text-font-sm text-center">
                        {!hasNotifyService
                          ? TEXT?.NO_SERVICE_FOR_VIEWING_FAVOURITED_NOTIFICATIONS
                          : TEXT?.NO_FAVOURITED_NOTIFICATIONS}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col w-width-full gap-gap-md Qlg:w-dashboard-right-card-width Qlg:absolute Qlg:h-height-full Qlg:right-right-base">
            <div className="flex-1 min-h-height-base">
              <WeeklySchedulesCard
                header={{
                  title: `${TEXT?.CHANNEL}s`,
                  icon: ICONS.CHANNEL_SETUP,
                }}
                noServicesText="Service for viewing channels is not assigned."
                list={{
                  loading: loading || channelsLoading,
                  emptyText: canViewChannels
                    ? "There are no channels"
                    : "Service for viewing channels is not assigned.",
                  data: canViewChannels ? channels : [],
                  getTitle: (row) => row?.name || row?.channel_name,
                  getID: (row) => row?.id,
                  titleLabel: `${TEXT?.CHANNEL} ${TEXT?.NAME.toLowerCase()}`,
                  rightIcon: (row) => getChannelIcon(row?.provider),
                  handleClick: (channelId) => {
                    navigate(
                      `/home/notification_dashboard/channel_view_all/channel_detail_page/${channelId}`,
                    );
                  },
                }}
                services={services}
                GlobalConfiguration={GlobalConfiguration}
                formatPendingCount={formatPendingCount}
                onFeatureNotAssigned={(missingServices) =>
                  navigate("/feature_not_assigned", {
                    state: { unavailableService: missingServices },
                  })
                }
                actions={[
                  {
                    label: `${TEXT?.CREATE} ${TEXT?.CHANNEL?.toLowerCase()}`,
                    service: ["CChannel"],
                    onClick: handleCreateChannel,
                    enabledBgColor: "var(--primary-nav-act)",
                    enabledTextColor: "var(--text-primary)",
                    hoverOption: "white",
                    fullWidthRequired: true,
                  },
                  {
                    label: `${TEXT?.VIEW} ${TEXT?.CHANNEL.toLowerCase()}s`,
                    service: ["VChannel"],
                    onClick: handleViewChannels,
                    enabledBgColor: "var(--primary-nav-act)",
                    enabledTextColor: "var(--text-primary)",
                    hoverOption: "white",
                    fullWidthRequired: true,
                    isDisabled:
                      channels?.length === GlobalConfiguration.lengthCheckZero,
                  },
                ]}
                isFullHeightRequired
              />
            </div>
            <CommonMoreActionsCard
              title={TEXT?.MORE_ACTIONS}
              actions={[
                {
                  label: `${TEXT?.NOTIFICATION_CENTER}`,
                  service: "Notify",
                  onClick: () => {
                    if (services?.includes("Notify")) {
                      navigate("/settings/notification_center");
                    } else {
                      navigate("/feature_not_assigned", {
                        state: { unavailableService: "Notify" },
                      });
                    }
                  },
                },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationDashboard;
